import { Eye, PlayCircle, Search, PauseCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { messageLisible } from '@/shared/api/erreurs';
import {
  useEntreprise,
  useEntreprises,
  useReactiverEntreprise,
  useSuspendreEntreprise,
  type EntrepriseAdminDto,
} from '@/shared/api/hooksAdmin';
import { ACTES } from '@/shared/audit/actesSensibles';
import { DialogueActeSensible } from '@/shared/audit/DialogueActeSensible';
import { date, entier } from '@/shared/lib/utils';
import { Bouton } from '@/shared/ui/Bouton';
import { Saisie } from '@/shared/ui/Champ';
import {
  ContenuDialogue,
  CorpsDialogue,
  DescriptionDialogue,
  Dialogue,
  EnTeteDialogue,
  TitreDialogue,
} from '@/shared/ui/Dialogue';
import { EnTeteSection, Pastille, Table, type ColonneTable } from '@/shared/ui/Table';

type ActeEnCours =
  | { type: 'suspendre'; entreprise: EntrepriseAdminDto }
  | { type: 'reactiver'; entreprise: EntrepriseAdminDto };

/**
 * Entreprises de la plateforme.
 *
 * LA LISTE N'EST PAS TRACEE, LA FICHE L'EST. La distinction est deliberee et documentee
 * cote back : tracer l'ecran d'accueil de l'outil noierait le journal sous des entrees
 * sans valeur, et rendrait invisibles celles qui comptent. Ouvrir une fiche, en revanche,
 * designe une entreprise precise — c'est un acte, et il laisse une trace.
 *
 * L'interface le DIT, au lieu de le faire discretement : le bouton s'appelle « Consulter
 * (trace) » et le panneau rappelle que la consultation est journalisee. Un acces encadre
 * dont l'operateur ignorerait qu'il est encadre n'aurait aucune vertu.
 */
export default function EntreprisesPage() {
  const [recherche, setRecherche] = useState('');
  const [suspendues, setSuspendues] = useState<boolean | undefined>(undefined);
  const [ficheOuverte, setFicheOuverte] = useState<string | null>(null);
  const [acte, setActe] = useState<ActeEnCours | null>(null);

  const { data, isPending, isError, error, refetch } = useEntreprises({
    recherche: recherche.trim() || undefined,
    suspendues,
  });

  const suspendre = useSuspendreEntreprise();
  const reactiver = useReactiverEntreprise();

  const colonnes: readonly ColonneTable<EntrepriseAdminDto>[] = [
    {
      cle: 'raisonSociale',
      entete: 'Entreprise',
      rendu: (ligne) => (
        <span className="flex items-center gap-2">
          <span className="font-medium text-texte">{ligne.raisonSociale}</span>
          {ligne.estSuspendue ? <Pastille ton="danger">Suspendue</Pastille> : null}
        </span>
      ),
    },
    {
      cle: 'siret',
      entete: 'SIRET',
      secondaire: true,
      rendu: (ligne) => <span className="tabular-nums text-texte-doux">{ligne.siret}</span>,
    },
    {
      cle: 'abonnement',
      entete: 'Abonnement',
      rendu: (ligne) =>
        ligne.statutAbonnement ? (
          <Pastille ton={ligne.accesOuvert ? 'succes' : 'alerte'}>
            {ligne.planAbonnement} · {ligne.statutAbonnement}
          </Pastille>
        ) : (
          <span className="text-texte-faible">—</span>
        ),
    },
    {
      cle: 'membres',
      entete: 'Membres',
      numerique: true,
      secondaire: true,
      rendu: (ligne) => entier(ligne.nombreMembres),
    },
    {
      cle: 'appels',
      entete: 'Appels API',
      numerique: true,
      secondaire: true,
      rendu: (ligne) => entier(ligne.appelsApiPeriodeCourante),
    },
    {
      cle: 'creeLe',
      entete: 'Creee le',
      secondaire: true,
      rendu: (ligne) => <span className="text-texte-doux">{date(ligne.creeLe)}</span>,
    },
    {
      cle: 'actions',
      entete: '',
      rendu: (ligne) => (
        <div className="flex justify-end gap-1">
          <Bouton
            variante="secondaire"
            taille="sm"
            onClick={() => setFicheOuverte(ligne.entrepriseId ?? null)}
          >
            <Eye />
            Consulter (trace)
          </Bouton>

          {ligne.estSuspendue ? (
            <Bouton
              variante="discret"
              taille="sm"
              onClick={() => setActe({ type: 'reactiver', entreprise: ligne })}
            >
              <PlayCircle />
              Reactiver
            </Bouton>
          ) : (
            <Bouton
              variante="discret"
              taille="sm"
              onClick={() => setActe({ type: 'suspendre', entreprise: ligne })}
            >
              <PauseCircle />
              Suspendre
            </Bouton>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <EnTeteSection
        titre="Entreprises"
        description="Toutes les entreprises de la plateforme."
        sensible="Ouvrir la fiche d’une entreprise ecrit une entree au journal d’audit. La liste ci-dessous, non."
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-xs flex-1">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-texte-faible"
            aria-hidden
          />
          <Saisie
            type="search"
            value={recherche}
            onChange={(evenement) => setRecherche(evenement.target.value)}
            placeholder="Raison sociale ou SIRET…"
            aria-label="Rechercher une entreprise"
            className="pl-7"
          />
        </div>

        <div className="flex gap-1" role="group" aria-label="Filtrer par etat">
          {(
            [
              { valeur: undefined, libelle: 'Toutes' },
              { valeur: false, libelle: 'Actives' },
              { valeur: true, libelle: 'Suspendues' },
            ] as const
          ).map((filtre) => (
            <Bouton
              key={filtre.libelle}
              variante={suspendues === filtre.valeur ? 'secondaire' : 'discret'}
              taille="sm"
              aria-pressed={suspendues === filtre.valeur}
              onClick={() => setSuspendues(filtre.valeur)}
            >
              {filtre.libelle}
            </Bouton>
          ))}
        </div>
      </div>

      <Table
        colonnes={colonnes}
        lignes={data}
        cleLigne={(ligne) => ligne.entrepriseId ?? ''}
        chargement={isPending}
        erreur={isError ? error : undefined}
        onReessayer={() => void refetch()}
        messageVide="Aucune entreprise ne correspond."
      />

      {ficheOuverte ? (
        <FicheEntreprise entrepriseId={ficheOuverte} onFermer={() => setFicheOuverte(null)} />
      ) : null}

      {acte?.type === 'suspendre' ? (
        <DialogueActeSensible
          acte={ACTES.entrepriseSuspendue}
          cible={acte.entreprise.raisonSociale ?? ''}
          enCours={suspendre.isPending}
          onFermer={() => setActe(null)}
          onConfirmer={(motif) =>
            suspendre.mutate(
              { entrepriseId: acte.entreprise.entrepriseId as string, motif },
              {
                onSuccess: () => {
                  toast.success('Entreprise suspendue', { description: 'Acte consigne au journal.' });
                  setActe(null);
                },
                onError: (erreur) =>
                  toast.error('Suspension impossible', { description: messageLisible(erreur) }),
              },
            )
          }
        />
      ) : null}

      {acte?.type === 'reactiver' ? (
        <DialogueActeSensible
          acte={ACTES.entrepriseReactivee}
          cible={acte.entreprise.raisonSociale ?? ''}
          enCours={reactiver.isPending}
          onFermer={() => setActe(null)}
          onConfirmer={() =>
            reactiver.mutate(acte.entreprise.entrepriseId as string, {
              onSuccess: () => {
                toast.success('Entreprise reactivee', { description: 'Acte consigne au journal.' });
                setActe(null);
              },
              onError: (erreur) =>
                toast.error('Reactivation impossible', { description: messageLisible(erreur) }),
            })
          }
        />
      ) : null}
    </div>
  );
}

/**
 * Fiche d'une entreprise.
 *
 * Son ouverture DECLENCHE la requete auditee — c'est le sens de `useEntreprise`, qui
 * n'est active que lorsque cette boite existe. Elle n'affiche volontairement aucune
 * donnee de client final : piloter la plateforme ne demande pas de lire le carnet
 * d'adresses de ses clients (§0.3).
 */
function FicheEntreprise({
  entrepriseId,
  onFermer,
}: {
  entrepriseId: string;
  onFermer: () => void;
}) {
  const { data, isPending, isError, error } = useEntreprise(entrepriseId);

  return (
    <Dialogue open onOpenChange={(ouvert) => !ouvert && onFermer()}>
      <ContenuDialogue>
        <EnTeteDialogue>
          <TitreDialogue>{data?.raisonSociale ?? 'Fiche entreprise'}</TitreDialogue>
          <DescriptionDialogue>
            Consultation journalisee — votre compte et l’horodatage sont consignes.
          </DescriptionDialogue>
        </EnTeteDialogue>

        <CorpsDialogue>
          {isPending ? (
            <p className="text-texte-doux">Chargement…</p>
          ) : isError ? (
            <p className="text-danger">{messageLisible(error)}</p>
          ) : (
            <dl className="space-y-1.5">
              {(
                [
                  ['SIRET', data.siret],
                  ['Creee le', date(data.creeLe)],
                  ['Membres', entier(data.nombreMembres)],
                  ['Plan', data.planAbonnement ?? '—'],
                  ['Statut abonnement', data.statutAbonnement ?? '—'],
                  ['Acces', data.accesOuvert ? 'Ouvert' : 'Ferme'],
                  ['Appels API (mois)', entier(data.appelsApiPeriodeCourante)],
                  ['Etat', data.estSuspendue ? 'Suspendue' : 'Active'],
                ] as const
              ).map(([libelle, valeur]) => (
                <div key={libelle} className="flex justify-between gap-4 border-b border-bordure pb-1">
                  <dt className="text-texte-doux">{libelle}</dt>
                  <dd className="text-texte">{valeur}</dd>
                </div>
              ))}
            </dl>
          )}

          <p className="mt-4 text-texte-faible">
            Cette fiche ne montre aucune donnee de client final. Y acceder releve d’une
            demande RGPD, traitee depuis l’ecran dedie.
          </p>
        </CorpsDialogue>
      </ContenuDialogue>
    </Dialogue>
  );
}
